/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {useEffect, useState} from 'react';
/* eslint-disable react/jsx-props-no-spreading */
import {
  RefreshCw,
  Calendar,
  Tag,
  DollarSign,
  PenLine,
  Check,
  PlusCircle,
} from 'lucide-react';
import DatePicker from 'components/Datepicker';
import Select from 'components/UI/Select';
import dayjs from 'dayjs';
import {Controller, SubmitHandler, useForm} from 'react-hook-form';
import movementsService from 'services/movements';
import {useLocalStorage} from 'usehooks-ts';

type Props = {
  defaultValues: CreateMovement | Movement;
  onSaving: () => void;
  onSave: (addAnother: boolean) => void;
  onError: () => void;
  categories: Category[];
};

function MovementForm({
  defaultValues,
  onSaving,
  onError,
  onSave,
  categories,
}: Props) {
  const [addAnother, setAddAnother] = useState<boolean>(false);

  const [saving, setSaving] = useState<boolean>();
  const [lastDate, setLastDate] = useLocalStorage<Date>(
    'lastDate',
    dayjs().startOf('day').toDate(),
  );
  const {
    control,
    setValue,
    watch,
    register,
    handleSubmit,
    formState: {isValid},
  } = useForm<CreateMovement | Movement>({
    defaultValues: {
      ...defaultValues,
      date:
        defaultValues.date ||
        lastDate ||
        (dayjs().startOf('day').toDate() as Date),
    },
  });

  const watchDate = watch('date');

  useEffect(() => {
    if (watchDate) {
      setLastDate(watchDate);
    }
  }, [watchDate]);

  const storeMovement = async (persistMovement: CreateMovement | Movement) => {
    onSaving();
    setSaving(true);
    const rsp = await movementsService.store(persistMovement);
    if (rsp.status) {
      onSave(addAnother);
    } else {
      onError();
    }
    setSaving(false);
  };

  const onSubmit: SubmitHandler<CreateMovement | Movement> = storeMovement;

  const isEdit = 'id' in defaultValues && defaultValues.id;

  const categoriesItems = categories.map((category: Category) => ({
    label: category.name,
    value: category.id,
  }));

  const categoriesItemRenderer = (item: SelectItem<number>) => {
    const selectedCategory = categories.find(
      (category) => category.id === item.value,
    );
    return (
      <div className='p-3 text-sm flex items-center justify-between hover:bg-secondary/50 transition-colors'>
        <span className='font-medium'>
          {selectedCategory?.name || item.label}
        </span>
        {selectedCategory && (
          <span className='text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-primary/10 text-primary'>
            {selectedCategory.category_type.name}
          </span>
        )}
      </div>
    );
  };

  const inputClass =
    'w-full bg-white focus:border-primary/20 focus:bg-white focus:ring-4 focus:ring-primary/10 rounded-xl p-4 text-sm font-medium transition-all placeholder:text-muted-foreground/50 border border-slate-200';

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
      <div className='flex flex-col gap-4'>
        {/* Concept Input */}
        <div className='space-y-2'>
          <label
            htmlFor='concept'
            className='text-xxs uppercase tracking-widest font-bold text-muted-foreground px-1 flex items-center gap-2'
          >
            <PenLine size={12} /> What did you buy?
          </label>
          <input
            id='concept'
            tabIndex={0}
            autoComplete='off'
            className={inputClass}
            placeholder='Coffee, Rent, Groceries...'
            {...register('concept', {required: true})}
          />
        </div>

        {/* Category & Amount Grid */}
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
          <div className='space-y-2'>
            <label
              htmlFor='category_id'
              className='text-xxs uppercase tracking-widest font-bold text-muted-foreground px-1 flex items-center gap-2'
            >
              <Tag size={12} /> Category
            </label>
            <Controller
              name='category_id'
              control={control}
              rules={{required: true, validate: (value) => value !== 0}}
              render={({field}) => (
                <div className='glass rounded-xl shadow-sm'>
                  <Select<SelectItem<number>>
                    items={categoriesItems}
                    placeholderRenderer={
                      <div className='p-4 text-sm text-muted-foreground/50 font-medium'>
                        Select Category...
                      </div>
                    }
                    itemRenderer={categoriesItemRenderer}
                    value={
                      categoriesItems.find((x) => x.value === field.value) ||
                      null
                    }
                    onChange={(v) => {
                      field.onChange(v.value);
                    }}
                  />
                </div>
              )}
            />
          </div>

          <div className='space-y-2'>
            <label
              htmlFor='amount'
              className='text-xxs uppercase tracking-widest font-bold text-muted-foreground px-1 flex items-center gap-2'
            >
              <DollarSign size={12} /> Amount
            </label>
            <input
              id='amount'
              tabIndex={0}
              type='number'
              inputMode='decimal'
              autoComplete='off'
              className={inputClass}
              placeholder='0.00'
              {...register('amount', {
                required: true,
                validate: (value) => value !== 0,
              })}
            />
          </div>
        </div>

        {/* Date Input */}
        <div className='space-y-2'>
          <label
            htmlFor='date'
            className='text-xxs uppercase tracking-widest font-bold text-muted-foreground px-1 flex items-center gap-2'
          >
            <Calendar size={12} /> When?
          </label>
          <Controller
            name='date'
            control={control}
            rules={{required: true}}
            render={({field}) => (
              <div className='space-y-3'>
                <div className='glass rounded-xl p-1 shadow-sm'>
                  <DatePicker
                    defaultValue={field.value}
                    placeholder='Select Date'
                    {...field}
                  />
                </div>
                <div className='flex gap-2 px-1'>
                  <button
                    type='button'
                    className='text-[10px] px-4 py-2 rounded-full bg-secondary text-secondary-foreground font-bold active:scale-95 transition-all'
                    onClick={() =>
                      setValue('date', dayjs().startOf('day').toDate(), {
                        shouldDirty: true,
                        shouldValidate: true,
                        shouldTouch: true,
                      })
                    }
                  >
                    Today
                  </button>
                  <button
                    type='button'
                    className='text-[10px] px-4 py-2 rounded-full bg-secondary text-secondary-foreground font-bold active:scale-95 transition-all'
                    onClick={() =>
                      setValue(
                        'date',
                        dayjs().subtract(1, 'day').startOf('day').toDate(),
                        {shouldValidate: true, shouldTouch: true},
                      )
                    }
                  >
                    Yesterday
                  </button>
                </div>
              </div>
            )}
          />
        </div>

        {/* Floating Action Bar */}
        <div className='fixed bottom-6 left-0 right-0 z-50 pointer-events-none'>
          <div className='inner flex justify-end px-6 items-center'>
            <div className='glass flex items-center gap-8 px-8 py-3 rounded-full shadow-2xl border border-white/20 animate-in fade-in slide-in-from-bottom-4 duration-500 pointer-events-auto'>
              <button
                type='submit'
                onClick={() => setAddAnother(false)}
                disabled={!isValid || saving}
                className='flex flex-col items-center gap-1 text-muted-foreground hover:text-primary transition-all duration-200 disabled:opacity-30'
              >
                {saving && !addAnother ? (
                  <RefreshCw className='animate-spin' size={22} />
                ) : (
                  <Check size={22} strokeWidth={2.5} />
                )}
                <span className='text-[10px] font-medium'>Save</span>
              </button>
              {!isEdit && (
                <button
                  type='submit'
                  onClick={() => setAddAnother(true)}
                  disabled={!isValid || saving}
                  className='flex flex-col items-center gap-1 text-muted-foreground hover:text-primary transition-all duration-200 disabled:opacity-30'
                >
                  {saving && addAnother ? (
                    <RefreshCw className='animate-spin' size={22} />
                  ) : (
                    <PlusCircle size={22} strokeWidth={2.5} />
                  )}
                  <span className='text-[10px] font-medium'>Save +</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}

export default MovementForm;
